<template>
  <v-dialog
    ref="loadingDialog"
    data-qa="loading_dialog"
    :value="active"
    persistent
    max-width="300"
    content-class="loading_dialog"
  >
    <v-card :color="customColoursLight.primary">
      <v-card-text class="white--text">
        {{ $m.loading_wait }}
        <v-progress-linear indeterminate color="white" class="mb-0" />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import appStore from "@/store/modules/appStore";
import { makeRef } from "@/components/composables/componentTools";
import { customColoursLight } from "@/plugins/vuetify";
import { computed, ref, watch } from "vue";

/**
 * Unfortunately when this dialog comes and goes quickly the overlay can sometimes get stuck active. Presumably this is
 * an issue with Vuetify 2.0 - worth removing this complexity once it matures a bit and seeing if the issue still exists.
 */
const minimumLoadTimeMs = 300;
const loadingStartTimeMs = makeRef(0);
const loadingTimer = makeRef(0);
const overlayTimer = makeRef(0);
const active = makeRef(false);
const loadingDialog = ref<{ removeOverlay: () => void } | null>(null);

const loading = computed(() => appStore.state.loading && !document.hidden);

/**
 * @desc Show loafing dialog and ensure the it appears for a minimum amount of time to prevent flashing.
 */
function loadingWatch(val: boolean) {
  clearTimeout(overlayTimer.value);
  clearTimeout(loadingTimer.value);

  if (val) {
    active.value = true;
    loadingStartTimeMs.value = Date.now();
  } else {
    const now = Date.now();
    const timeSinceLoadingStarted = now - loadingStartTimeMs.value;

    if (timeSinceLoadingStarted < minimumLoadTimeMs) {
      loadingTimer.value = +setTimeout(() => {
        active.value = false;

        removeOverlayAfterDelay();
      }, minimumLoadTimeMs - timeSinceLoadingStarted);
    } else {
      active.value = false;
      removeOverlayAfterDelay();
    }
  }
}

function removeOverlayAfterDelay() {
  overlayTimer.value = +setTimeout(() => {
    active.value = false;
    loadingDialog.value?.removeOverlay();
  }, 100);
}

watch(loading, loadingWatch, { immediate: true });
</script>
